import { Fragment, useEffect, useState } from 'react'
import { Button, Tree, TreeItem, TreeItemContent } from 'react-aria-components'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GiVendingMachine } from 'react-icons/gi'
import {
    MdAccountBalance,
    MdAssessment,
    MdKeyboardArrowRight,
    MdMenuBook,
    MdPeople,
    MdReceipt,
    MdSettings,
    MdStore,
} from 'react-icons/md'

import { Divider } from 'app/ui'

import { useUserStore } from 'app/stores/app/user'
import { permissions, urls } from 'app/utils/constants'
import { joinStyles } from 'app/utils/theme'

import * as styles from './Sidebar.css'

export const Sidebar = (): JSX.Element => {
    const { t } = useTranslation()

    const user = useUserStore((state) => state.user)
    const isManager = useUserStore((state) => state.isManager)
    const hasPermission = useUserStore((state) => state.hasPermission)

    const [expandedKeys, setExpandedKeys] = useState(new Set([]))

    const { pathname } = window.location
    const actualItem = `/${pathname.split('/')[1]}`

    const park = {
        name: t('park'),
        icon: <MdStore />,
        index: urls.park.INDEX,
        pages: [
            {
                name: t('managers'),
                link: urls.park.managers.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.park.managers.READ),
            },
            {
                name: t('clients'),
                link: urls.park.clients.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.park.clients.READ),
            },
            {
                name: t('sites'),
                link: urls.park.sites.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.park.sites.READ),
            },
        ],
    }

    const machines = {
        name: t('machines'),
        icon: <GiVendingMachine />,
        index: urls.machines.INDEX,
        pages: [
            {
                name: t('all'),
                link: urls.machines.ALL,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.machines.all.READ),
            },
            {
                name: t('microwaves'),
                link: urls.machines.microwaves.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.machines.all.READ),
            },
            {
                name: t('planograms'),
                link: urls.machines.planograms.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.machines.planograms.READ),
            },
        ],
    }

    const catalogue = {
        name: t('catalogue'),
        icon: <MdMenuBook />,
        index: urls.catalogue.INDEX,
        pages: [
            {
                name: t('suppliers'),
                link: urls.catalogue.suppliers.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.catalogue.suppliers.READ),
            },
            {
                name: t('products'),
                link: urls.catalogue.product.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.catalogue.products.READ),
            },
            {
                name: t('priceTables'),
                link: urls.catalogue.PRICE_TABLES,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.catalogue.priceTable.READ),
            },
            {
                name: t('discounts'),
                link: urls.catalogue.DISCOUNTS,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.catalogue.discounts.READ),
            },
        ],
    }

    const transactions = {
        name: t('transactions'),
        icon: <MdAccountBalance />,
        index: urls.transactions.INDEX,
        pages: [
            {
                name: t('sales'),
                link: urls.transactions.sales.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.transactions.sales.READ),
            },
            {
                name: t('refunds'),
                link: urls.transactions.REFUNDS,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.transactions.refunds.READ),
            },
            {
                name: t('refills'),
                link: urls.transactions.REFILLS,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.transactions.refills.READ),
            },
            {
                // TODO: Add the permissions
                name: t('blockedCards'),
                link: urls.transactions.BLOCKED_CARDS,
                canView: user.is_superuser || user.is_staff || isManager,
            },
        ],
    }

    const operations = {
        name: t('operations'),
        icon: <MdReceipt />,
        index: urls.operations.INDEX,
        pages: [
            {
                // TODO: Add the permissions
                name: t('orders'),
                link: urls.operations.orders.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    (isManager &&
                        user.profil.client.some(
                            (manager) => manager.has_order,
                        )),
            },

            {
                name: t('deliveries'),
                link: urls.operations.DELIVERIES,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.operations.deliveries.READ),
            },
            {
                name: t('interventions'),
                link: urls.operations.INTERVENTIONS,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.operations.interventions.READ),
            },
            {
                name: t('incidents'),
                link: urls.operations.INCIDENTS,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.operations.incidents.READ),
            },
            {
                name: t('incidentsRabbitmq'),
                link: urls.operations.INCIDENTS_RABBITMQ,
                canView: user.is_superuser,
            },
            {
                name: t('encodedProducts'),
                link: urls.operations.ENCODED_PRODUCTS,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.operations.encodedProducts.READ),
            },
            {
                name: t('destocking'),
                link: urls.operations.DESTOCKING,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.operations.destocking.READ),
            },
        ],
    }

    const reports = {
        name: t('reports'),
        icon: <MdAssessment />,
        index: urls.reports.INDEX,
        pages: [
            {
                name: t('stock'),
                link: urls.reports.STOCK,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.machines.stock.READ),
            },
            {
                // TODO: Add the permissions
                name: t('supply'),
                link: urls.reports.SUPPLY,
                canView: user.is_superuser || user.is_staff || isManager,
            },
            {
                // TODO: Add the permissions
                name: t('sale'),
                link: urls.reports.sale.LIST,
                canView: user.is_superuser || user.is_staff || isManager,
            },
            {
                // TODO: Add the permissions
                name: t('service'),
                link: urls.reports.service.LIST,
                canView: user.is_superuser || user.is_staff || isManager,
            },
            {
                // TODO: Add the permissions
                name: t('salesLog'),
                link: urls.reports.vat.LIST,
                canView: user.is_superuser || user.is_staff || isManager,
            },
        ],
    }

    const users = {
        name: t('users'),
        icon: <MdPeople />,
        index: urls.users.INDEX,
        pages: [
            {
                name: t('profiles'),
                link: urls.users.profiles.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.users.profiles.READ),
            },
            {
                name: 'Konsole',
                link: urls.users.konsole.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.users.konsole.READ),
            },
            {
                name: t('technicians'),
                link: urls.users.technicians.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.users.technicians.READ),
            },
            {
                name: t('deliveryMen'),
                link: urls.users.deliveryMen.LIST,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.users.deliveryMen.READ),
            },
            {
                // TODO: Add the permissions
                name: t('services'),
                link: urls.users.services.LIST,
                canView: user.is_superuser || user.is_staff || isManager,
            },
            {
                name: 'Konvives',
                link: urls.users.KONVIVES,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.users.konvives.READ),
            },
        ],
    }

    const settings = {
        name: t('settings'),
        icon: <MdSettings />,
        index: urls.settings.INDEX,
        pages: [
            {
                name: t('theme'),
                link: urls.settings.THEME,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.settings.theme.READ),
            },
            {
                name: t('categories'),
                link: urls.settings.CATEGORIES,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.settings.productCategories.READ),
            },
            {
                name: t('email'),
                link: urls.settings.EMAIL,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.settings.email.READ),
            },
            {
                name: t('ticket'),
                link: urls.settings.TICKET,
                canView:
                    user.is_superuser ||
                    user.is_staff ||
                    hasPermission(permissions.settings.ticket.READ),
            },
        ],
    }

    const groups = [
        park,
        machines,
        catalogue,
        transactions,
        operations,
        reports,
        users,
        settings,
    ].filter((group) => {
        group.pages = group.pages.filter((page) => page.canView)

        return group.pages.length > 0
    })

    // Reset the expanded keys when the path change
    useEffect(() => {
        setExpandedKeys(new Set([actualItem]))
    }, [actualItem])

    return (
        <nav className={styles.root}>
            <Tree
                expandedKeys={expandedKeys}
                onExpandedChange={setExpandedKeys}
                aria-label={t('navigationBar')}
            >
                {groups.map((group, index) => (
                    <Fragment key={group.index}>
                        <TreeItem
                            id={group.index}
                            className={styles.item}
                            textValue={group.name}
                        >
                            {/* Group */}
                            <TreeItemContent>
                                <Button
                                    className={joinStyles(
                                        styles.itemButton,
                                        pathname.startsWith(group.index) &&
                                            styles.itemSelected,
                                    )}
                                    slot='chevron'
                                >
                                    {group.icon}

                                    <span className={styles.itemName}>
                                        {group.name}
                                    </span>

                                    <MdKeyboardArrowRight
                                        className={styles.itemChevron}
                                    />
                                </Button>
                            </TreeItemContent>

                            {/* Pages */}
                            {group.pages.map((page) => (
                                <TreeItem
                                    key={page.link}
                                    textValue={page.name}
                                >
                                    <TreeItemContent>
                                        <Link
                                            className={joinStyles(
                                                styles.link,
                                                pathname.startsWith(
                                                    page.link,
                                                ) && styles.linkSelected,
                                            )}
                                            to={page.link}
                                        >
                                            {page.name}
                                        </Link>
                                    </TreeItemContent>
                                </TreeItem>
                            ))}
                        </TreeItem>

                        {/* Divider */}
                        {index + 1 < groups.length && (
                            <TreeItem
                                textValue={t('divider')}
                                isDisabled
                            >
                                <TreeItemContent>
                                    <Divider color='secondary' />
                                </TreeItemContent>
                            </TreeItem>
                        )}
                    </Fragment>
                ))}
            </Tree>
        </nav>
    )
}
